import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Streamer, StreamerTheme, AnimationAssignment, Game, GameAction, GiftGameRule,
  getStreamerThemes, setActiveTheme, setActiveGame,
  getAnimationAssignments, upsertAnimationAssignment, deleteAnimationAssignment,
  setPriorityGift,
  getGames, getStreamerGameActions, getGiftGameRules, createGiftGameRule, deleteGiftGameRule,
  createStreamerGameAction, updateStreamerGameAction, deleteStreamerGameAction,
  previewAnimation, previewAnimationMobile, animationFileUrl, overlayUrl,
  triggerManualAnimation, sendDynamicText,
  getQuestionQueue, queueTest,
  getQuestionQueueKeywords, updateQuestionQueueKeywords,
  getStreamerQueueTTS, setStreamerQueueTTS, getElevenLabsVoices,
  getMobileComments, updateMobileComments,
  updateBannerConfig,
} from './api';
import GiftPicker from './GiftPicker';
import { QueuePreview } from './QuestionQueuePanel';

interface Props {
  streamer: Streamer;
  onStreamerUpdated: () => void;
  isReadOnly: boolean;
  isMobile?: boolean;
}

const proxyGiftImg = (url: string) => `/api/gift-image?url=${encodeURIComponent(url)}`;
const emptyGameActionForm = () => ({ name: '', description: '', action_type: 'key', action_value: '', duration_ms: 100, gamepad_value: '' });

export default function RuleEditor({ streamer, onStreamerUpdated, isReadOnly, isMobile }: Props) {
  const [themes, setThemes] = useState<StreamerTheme[]>([]);
  const [activeThemeId, setActiveThemeIdState] = useState<number | null>(streamer.active_theme_id);
  const [assignments, setAssignments] = useState<AnimationAssignment[]>([]);

  const [games, setGames] = useState<Game[]>([]);
  const [activeGameId, setActiveGameIdState] = useState<number | null>(streamer.active_game_id);
  const [gameActions, setGameActions] = useState<GameAction[]>([]);
  const [giftGameRules, setGiftGameRules] = useState<GiftGameRule[]>([]);
  const [desktopContent, setDesktopContent] = useState<'theme' | 'game'>('theme');
  const [themeSearch, setThemeSearch] = useState('');
  const [gameSearch, setGameSearch] = useState('');
  const [showGameActionForm, setShowGameActionForm] = useState(false);
  const [editingGameAction, setEditingGameAction] = useState<GameAction | null>(null);
  const [gameActionForm, setGameActionForm] = useState(emptyGameActionForm());

  const [previewAnim, setPreviewAnim] = useState<AnimationAssignment | null>(null);
  const [queueCfg, setQueueCfg] = useState<Record<string, any> | null>(null);
  const [mobileCommentsState, setMobileCommentsState] = useState<{enabled: boolean; tts_enabled: boolean; config: Record<string, any> | null} | null>(null);
  const [queueTick, setQueueTick] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [obsStatus, setObsStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [obsMsg, setObsMsg] = useState('');
  const [mobileStatus, setMobileStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [mobileMsg, setMobileMsg] = useState('');
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);

  // Table de mixage — manual animation selection
  const [selectedManual, setSelectedManual] = useState<AnimationAssignment | null>(null);
  const [manualStatus, setManualStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [manualMsg, setManualMsg] = useState('');
  // Text dynamic form state (pre-filled from slot config, user fills text)
  const [dynText, setDynText] = useState('');
  const [dynColor, setDynColor] = useState('#ffffff');
  const [dynSize, setDynSize] = useState(48);
  const [dynFont, setDynFont] = useState('sans-serif');
  const [dynEffect, setDynEffect] = useState('fade');
  const [dynLoops, setDynLoops] = useState(1);
  const [dynDuration, setDynDuration] = useState(5);
  const [manualVolumes, setManualVolumes] = useState<Record<number, number>>({});
  const [previewTextAnim, setPreviewTextAnim] = useState<{
    text: string; color: string; size: number; font: string;
    effect: string; loops: number; duration: number;
    pos_x: number; pos_y: number; pos_w: number; pos_h: number;
    key: number;
  } | null>(null);
  const shouldAutoPlayRef = useRef(false);

  const pickerCallbackRef = useRef<((name: string, id?: number | null) => void) | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const openPicker = (cb: (name: string, id?: number | null) => void) => {
    pickerCallbackRef.current = cb;
    setPickerOpen(true);
  };

  const loadThemes = useCallback(async () => {
    const r = await getStreamerThemes(streamer.id);
    setThemes(r.data);
  }, [streamer.id]);

  const loadAssignments = useCallback(async (themeId: number) => {
    const r = await getAnimationAssignments(streamer.id, themeId);
    setAssignments(r.data);
  }, [streamer.id]);

  const loadGames = useCallback(async () => {
    const r = await getGames(true);
    setGames(r.data);
  }, []);

  const loadGameContent = useCallback(async (gameId: number) => {
    const [actions, rules] = await Promise.all([
      getStreamerGameActions(streamer.id, gameId),
      getGiftGameRules(streamer.id, gameId),
    ]);
    setGameActions(actions.data);
    setGiftGameRules(rules.data);
  }, [streamer.id]);

  useEffect(() => {
    setActiveThemeIdState(streamer.active_theme_id);
    setActiveGameIdState(streamer.active_game_id);
    setAssignments([]); setGameActions([]); setGiftGameRules([]);
    setPreviewAnim(null); setIsPlaying(false);
    loadThemes(); loadGames();
  }, [streamer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQueueCfg = useCallback(async () => {
    try {
      const r = await getQuestionQueue(streamer.id);
      setQueueCfg(r.data.enabled ? (r.data.config || {}) : null);
    } catch { setQueueCfg(null); }
  }, [streamer.id]);

  const loadMobileComments = useCallback(async () => {
    try { const r = await getMobileComments(streamer.id); setMobileCommentsState(r.data); }
    catch { setMobileCommentsState(null); }
  }, [streamer.id]);

  const toggleMobileComments = async (patch: { enabled?: boolean; tts_enabled?: boolean }) => {
    const r = await updateMobileComments(streamer.id, patch);
    setMobileCommentsState(r.data);
  };

  useEffect(() => {
    if (activeThemeId) { loadAssignments(activeThemeId); loadQueueCfg(); loadMobileComments(); }
    else { setAssignments([]); setQueueCfg(null); setMobileCommentsState(null); }
  }, [activeThemeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeGameId) loadGameContent(activeGameId);
    else { setGameActions([]); setGiftGameRules([]); }
  }, [activeGameId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectTheme = async (themeId: number) => {
    if (activeThemeId === themeId && desktopContent !== 'theme') {
      setDesktopContent('theme');
      return;
    }
    setDesktopContent('theme');
    const next = activeThemeId === themeId ? null : themeId;
    await setActiveTheme(streamer.id, next);
    setActiveThemeIdState(next); setPreviewAnim(null); onStreamerUpdated();
    window.dispatchEvent(new CustomEvent('nslive-active-content-change', {
      detail: {
        streamerId: streamer.id,
        activeThemeName: next ? themes.find(t => t.id === next)?.name ?? null : null,
      },
    }));
  };

  const handleMobileToggleTheme = async (themeId: number, activate: boolean) => {
    const next = activate ? themeId : null;
    await setActiveTheme(streamer.id, next);
    setActiveThemeIdState(next); setPreviewAnim(null); onStreamerUpdated();
  };

  const handleSelectGame = async (gameId: number) => {
    if (activeGameId === gameId && desktopContent !== 'game') {
      setDesktopContent('game');
      return;
    }
    setDesktopContent('game');
    const next = activeGameId === gameId ? null : gameId;
    await setActiveGame(streamer.id, next);
    setActiveGameIdState(next); onStreamerUpdated();
    window.dispatchEvent(new CustomEvent('nslive-active-content-change', {
      detail: {
        streamerId: streamer.id,
        activeGameName: next ? games.find(g => g.id === next)?.name ?? null : null,
      },
    }));
  };

  const handleAssign = async (themeAnimId: number, giftName: string, tiktokGiftId?: number | null) => {
    await upsertAnimationAssignment(streamer.id, themeAnimId, giftName, tiktokGiftId);
    if (activeThemeId) await loadAssignments(activeThemeId);
  };

  const handleSetPriorityGift = async (themeAnimId: number, giftName: string | null, tiktokGiftId?: number | null) => {
    await setPriorityGift(streamer.id, themeAnimId, giftName, tiktokGiftId);
    if (activeThemeId) await loadAssignments(activeThemeId);
  };

  const handleUnassign = async (assignmentId: number) => {
    await deleteAnimationAssignment(assignmentId);
    if (activeThemeId) await loadAssignments(activeThemeId);
  };

  const handleSelectManual = (a: AnimationAssignment) => {
    setSelectedManual(a);
    setPreviewAnim(null);
    setIsPlaying(false);
    setManualStatus('idle');
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
    if (a.animation_type === 'text_dynamic') {
      setDynColor(a.text_color || '#ffffff');
      setDynSize(a.text_size || 48);
      setDynFont(a.text_font || 'sans-serif');
      setDynEffect(a.text_effect || 'fade');
      setDynLoops(a.text_loops || 1);
      setDynDuration(Math.round((a.text_duration || 5000) / 1000));
      setDynText('');
    }
  };

  const handleTriggerManual = async (a: AnimationAssignment) => {
    setSelectedManual(a);
    setPreviewAnim(null);
    setIsPlaying(false);
    setManualStatus('idle');
    setPreviewTextAnim(null);
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
    const vol = manualVolumes[a.theme_animation_id] ?? 0.8;
    if (a.animation_type === 'video') {
      shouldAutoPlayRef.current = true;
    } else if (a.animation_type === 'text') {
      const totalMs = (a.text_duration || 5000) * (a.text_loops || 1);
      setPreviewTextAnim({ text: a.text_content || '', color: a.text_color || '#fff', size: a.text_size || 48, font: a.text_font || 'sans-serif', effect: a.text_effect || 'fade', loops: a.text_loops || 1, duration: a.text_duration || 5000, pos_x: a.pos_x, pos_y: a.pos_y, pos_w: a.pos_w, pos_h: a.pos_h, key: Date.now() });
      setTimeout(() => setPreviewTextAnim(null), totalMs);
    }
    try { await triggerManualAnimation(streamer.id, a.theme_animation_id, vol); } catch {}
  };

  const handleDynTextPreview = (params: { text: string; color: string; size: number; font: string; effect: string; loops: number; duration: number; pos_x: number; pos_y: number; pos_w: number; pos_h: number }) => {
    const totalMs = params.duration * params.loops;
    setPreviewTextAnim({ ...params, key: Date.now() });
    setTimeout(() => setPreviewTextAnim(null), totalMs);
  };

  const handleAddGameRule = async (actionId: number, giftName: string, tiktokGiftId?: number | null) => {
    if (!activeGameId) return;
    await createGiftGameRule(streamer.id, activeGameId, giftName, actionId, tiktokGiftId);
    await loadGameContent(activeGameId);
  };

  const handleDeleteGameRule = async (ruleId: number) => {
    await deleteGiftGameRule(ruleId);
    if (activeGameId) await loadGameContent(activeGameId);
  };

  const openAddGameAction = () => {
    setEditingGameAction(null);
    setGameActionForm(emptyGameActionForm());
    setShowGameActionForm(true);
  };

  const openEditGameAction = (action: GameAction) => {
    setEditingGameAction(action);
    setGameActionForm({
      name: action.name,
      description: action.description || '',
      action_type: action.action_type || 'key',
      action_value: action.action_value || '',
      duration_ms: action.duration_ms || 100,
      gamepad_value: action.gamepad_value || '',
    });
    setShowGameActionForm(true);
  };

  const saveGameAction = async () => {
    if (!activeGameId || !gameActionForm.name.trim() || !gameActionForm.action_value.trim()) return;
    const data = {
      name: gameActionForm.name.trim(),
      description: gameActionForm.description.trim(),
      action_type: gameActionForm.action_type,
      action_value: gameActionForm.action_value.trim(),
      duration_ms: Number(gameActionForm.duration_ms) || 100,
      gamepad_value: (gameActionForm.gamepad_value || '').trim() || null,
    };
    if (editingGameAction) {
      await updateStreamerGameAction(streamer.id, editingGameAction.id, data as any);
    } else {
      await createStreamerGameAction(streamer.id, activeGameId, data as any);
    }
    setShowGameActionForm(false);
    setEditingGameAction(null);
    setGameActionForm(emptyGameActionForm());
    await loadGameContent(activeGameId);
  };

  const handleDeleteGameAction = async (action: GameAction) => {
    if (!activeGameId) return;
    if (!action.streamer_id) {
      window.alert("Cette action vient de la configuration par défaut. Modifie-la pour créer ta version personnalisée, ou ajoute une nouvelle action.");
      return;
    }
    if (!window.confirm(`Supprimer l'action personnalisée "${action.name}" ?`)) return;
    await deleteStreamerGameAction(streamer.id, action.id);
    await loadGameContent(activeGameId);
  };

  const previewPos = previewAnim
    ? { x: previewAnim.pos_x, y: previewAnim.pos_y, w: previewAnim.pos_w, h: previewAnim.pos_h }
    : null;

  const activeTheme = themes.find(t => t.id === activeThemeId);
  const activeGame = games.find(g => g.id === activeGameId);
  const themeQuery = themeSearch.trim().toLowerCase();
  const gameQuery = gameSearch.trim().toLowerCase();
  const filteredThemes = themeQuery
    ? themes.filter(t => `${t.name} ${t.slug}`.toLowerCase().includes(themeQuery))
    : themes;
  const filteredGames = gameQuery
    ? games.filter(g => `${g.name} ${g.slug}`.toLowerCase().includes(gameQuery))
    : games;
  const sideCardImageHeight = 118;
  const sideCardFooterStyle: React.CSSProperties = {
    minHeight: 56,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
  };
  const previewOverlayUrl = activeTheme?.overlay_file
    ? overlayUrl(activeTheme.overlay_file)
    : streamer.overlay_file ? overlayUrl(streamer.overlay_file) : null;

  const giftsByAction: Record<number, GiftGameRule[]> = {};
  for (const r of giftGameRules) {
    if (!giftsByAction[r.game_action_id]) giftsByAction[r.game_action_id] = [];
    giftsByAction[r.game_action_id].push(r);
  }

  if (isMobile) {
    return (
      <MobileRuleEditor
        themes={themes}
        streamer={streamer}
        onToggleTheme={handleMobileToggleTheme}
        reloadThemes={loadThemes}
        handleAssign={handleAssign}
        handleUnassign={handleUnassign}
        handleSetPriorityGift={handleSetPriorityGift}
        openPicker={openPicker}
        pickerOpen={pickerOpen}
        pickerCallbackRef={pickerCallbackRef}
        setPickerOpen={setPickerOpen}
      />
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>

      <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toggle Thèmes / Jeux — n'affiche qu'une liste à la fois */}
        <div style={{ display: 'flex', gap: 6, padding: '12px 12px 10px', borderBottom: '1px solid #e2e8f0' }}>
          {([['theme', '🎨 Thèmes'], ['game', '🎮 Jeux']] as const).map(([k, label]) => (
            <button key={k} onClick={() => setDesktopContent(k)}
              style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid', borderColor: desktopContent === k ? '#2563eb' : '#e2e8f0', background: desktopContent === k ? '#eff6ff' : '#fff', color: desktopContent === k ? '#2563eb' : '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.12s' }}>
              {label}
            </button>
          ))}
        </div>
        {desktopContent === 'theme' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 800 }}>Thèmes</div>
            <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700 }}>{filteredThemes.length}/{themes.length}</span>
          </div>
          <div style={{ padding: '0 12px 10px' }}>
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
              <input value={themeSearch} onChange={e => setThemeSearch(e.target.value)} placeholder="Rechercher un thème..."
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px 8px 28px', fontSize: 12, outline: 'none', background: '#f8fafc', color: '#0f172a' }} />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'scroll', scrollbarGutter: 'stable', padding: '0 10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredThemes.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 12, padding: 12, border: '1px dashed #e2e8f0', borderRadius: 8, textAlign: 'center' }}>{themeSearch ? 'Aucun résultat' : 'Aucun thème assigné.'}</div>
            ) : filteredThemes.map(t => {
              const active = activeThemeId === t.id;
              const selected = desktopContent === 'theme' && active;
              return (
                <button key={t.id} onClick={() => handleSelectTheme(t.id)}
                  style={{ width: '100%', minHeight: sideCardImageHeight + 57, flexShrink: 0, textAlign: 'left', display: 'flex', flexDirection: 'column', padding: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${selected ? '#2563eb' : active ? '#bfdbfe' : '#e2e8f0'}`, background: selected ? '#eff6ff' : '#fff', cursor: 'pointer', boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.10)' : '0 1px 2px rgba(0,0,0,0.04)', transition: 'all 0.12s' }}>
                  <div style={{ width: '100%', height: sideCardImageHeight, overflow: 'hidden', background: t.image_url ? '#f1f5f9' : '#f5f3ff', flexShrink: 0 }}>
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 }}>🎨</div>
                    )}
                  </div>
                  <div style={sideCardFooterStyle}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: selected ? '#1d4ed8' : '#0f172a', fontSize: 13, fontWeight: 800, lineHeight: 1.3, wordBreak: 'break-word' }}>{t.name}</div>
                      <div style={{ color: active ? '#2563eb' : '#94a3b8', fontSize: 11, fontWeight: 700, marginTop: 2 }}>{active ? 'Actif' : `${(t as any).animation_count ?? 0} animation${((t as any).animation_count ?? 0) !== 1 ? 's' : ''}`}</div>
                    </div>
                    {active && <span style={{ color: '#2563eb', fontSize: 15, fontWeight: 900 }}>✓</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {desktopContent === 'game' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 800 }}>Jeux</div>
            <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700 }}>{filteredGames.length}/{games.length}</span>
          </div>
          <div style={{ padding: '0 12px 10px' }}>
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
              <input value={gameSearch} onChange={e => setGameSearch(e.target.value)} placeholder="Rechercher un jeu..."
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px 8px 28px', fontSize: 12, outline: 'none', background: '#f8fafc', color: '#0f172a' }} />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'scroll', scrollbarGutter: 'stable', padding: '0 10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredGames.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 12, padding: 12, border: '1px dashed #e2e8f0', borderRadius: 8, textAlign: 'center' }}>{gameSearch ? 'Aucun résultat' : 'Aucun jeu disponible.'}</div>
            ) : filteredGames.map(g => {
              const active = activeGameId === g.id;
              const selected = desktopContent === 'game' && active;
              return (
                <button key={g.id} onClick={() => handleSelectGame(g.id)}
                  style={{ width: '100%', minHeight: sideCardImageHeight + 57, flexShrink: 0, textAlign: 'left', display: 'flex', flexDirection: 'column', padding: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${selected ? '#2563eb' : active ? '#bfdbfe' : '#e2e8f0'}`, background: selected ? '#eff6ff' : '#fff', cursor: 'pointer', boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.10)' : '0 1px 2px rgba(0,0,0,0.04)', transition: 'all 0.12s' }}>
                  <div style={{ width: '100%', height: sideCardImageHeight, overflow: 'hidden', background: g.image_url ? '#f1f5f9' : '#f5f3ff', flexShrink: 0 }}>
                    {g.image_url ? (
                      <img src={g.image_url} alt={g.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 }}>🎮</div>
                    )}
                  </div>
                  <div style={sideCardFooterStyle}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: selected ? '#1d4ed8' : '#0f172a', fontSize: 13, fontWeight: 800, lineHeight: 1.3, wordBreak: 'break-word' }}>{g.name}</div>
                      <div style={{ color: active ? '#2563eb' : '#94a3b8', fontSize: 11, fontWeight: 700, marginTop: 2 }}>{active ? 'Actif' : `${(g as any).action_count ?? 0} action${((g as any).action_count ?? 0) !== 1 ? 's' : ''}`}</div>
                    </div>
                    {active && <span style={{ color: '#2563eb', fontSize: 15, fontWeight: 900 }}>✓</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}
      </div>

      {/* ── Colonne gauche ─────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {desktopContent === 'theme' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: '#0f172a', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
                Paramétrage du thème
              </div>
              <div style={{ color: '#64748b', fontSize: 13 }}>
                {activeTheme ? activeTheme.name : 'Sélectionne un thème dans la colonne de gauche.'}
              </div>
            </div>
          )}

          {/* ── Grille d'assignations ── */}
          {desktopContent === 'theme' && activeThemeId && (() => {
            const bannerAnims = assignments.filter(a => a.animation_type === 'banner');
            const autoAnims = assignments.filter(a => (a.trigger_type ?? 'auto') === 'auto' && a.animation_type !== 'mobile_comments' && a.animation_type !== 'banner');
            const manualAnims = assignments.filter(a => a.trigger_type === 'manual');
            return (
              <div style={{ marginBottom: 24 }}>

                {activeThemeId && mobileCommentsState?.config && (
                  <div style={{ border: '1px solid #bae6fd', background: '#f0f9ff', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: '#0369a1', fontWeight: 900, fontSize: 13 }}>Commentaires mobile</div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>Affichage des commentaires sur l'overlay mobile</div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0f172a', fontWeight: 800, fontSize: 12 }}>
                      <input type="checkbox" checked={mobileCommentsState.enabled} onChange={e => toggleMobileComments({ enabled: e.target.checked })} />
                      Afficher
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0f172a', fontWeight: 800, fontSize: 12 }}>
                      <input type="checkbox" checked={mobileCommentsState.tts_enabled} onChange={e => toggleMobileComments({ tts_enabled: e.target.checked })} />
                      Lecture
                    </label>
                  </div>
                )}

                {bannerAnims.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {bannerAnims.map(a => (
                      <BannerConfigCard key={a.theme_animation_id} slot={a} streamerId={streamer.id} onSaved={() => activeThemeId ? loadAssignments(activeThemeId) : undefined} />
                    ))}
                  </div>
                )}

                {/* Section manuelle — EN PREMIER */}
                {manualAnims.length > 0 && (() => {
                  const dynAnims = manualAnims.filter(a => a.animation_type === 'text_dynamic');
                  const videoManuals = manualAnims.filter(a => a.animation_type === 'video');
                  const audioManuals = manualAnims.filter(a => a.animation_type === 'audio');
                  const textManuals = manualAnims.filter(a => a.animation_type === 'text');
                  return (
                    <>
                      <div style={{ color: '#7c3aed', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>🎛️</span> Table de mixage — déclenchées en live
                      </div>

                      {/* Texte dynamique — pleine largeur avec formulaire inline */}
                      {dynAnims.map(a => (
                        <TextDynamicInlineCard key={a.theme_animation_id} slot={a} streamerId={streamer.id} onPreview={handleDynTextPreview} />
                      ))}

                      {/* Groupe Vidéo */}
                      {videoManuals.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ color: '#2563eb', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>🎬 Vidéo</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {videoManuals.map(a => (
                              <ManualPadCard key={a.theme_animation_id} slot={a}
                                isActive={selectedManual?.theme_animation_id === a.theme_animation_id}
                                onTrigger={() => handleTriggerManual(a)}
                                volume={manualVolumes[a.theme_animation_id] ?? 0.8}
                                onVolumeChange={v => setManualVolumes(prev => ({ ...prev, [a.theme_animation_id]: v }))} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Groupe Son */}
                      {audioManuals.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ color: '#16a34a', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>🔊 Son</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {audioManuals.map(a => (
                              <ManualPadCard key={a.theme_animation_id} slot={a}
                                isActive={selectedManual?.theme_animation_id === a.theme_animation_id}
                                onTrigger={() => handleTriggerManual(a)}
                                volume={manualVolumes[a.theme_animation_id] ?? 0.8}
                                onVolumeChange={v => setManualVolumes(prev => ({ ...prev, [a.theme_animation_id]: v }))} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Groupe Texte statique */}
                      {textManuals.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ color: '#ea580c', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>📝 Texte</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {textManuals.map(a => (
                              <ManualPadCard key={a.theme_animation_id} slot={a}
                                isActive={selectedManual?.theme_animation_id === a.theme_animation_id}
                                onTrigger={() => handleTriggerManual(a)}
                                volume={null}
                                onVolumeChange={() => {}} />
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ marginBottom: 6 }} />
                    </>
                  );
                })()}

                {/* Section automatique */}
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🎁</span> Automatique — déclenchées par cadeaux
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, alignItems: 'start' }}>
                  {autoAnims.map(a => {
                    const card = (
                      <AnimAssignCard
                        slot={a}
                        streamerId={streamer.id}
                        isActive={previewAnim?.theme_animation_id === a.theme_animation_id}
                        onPreview={() => {
                          setPreviewAnim(a); setSelectedManual(null); setIsPlaying(false);
                          if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
                        }}
                        openPicker={openPicker}
                        onAssign={(name, tid) => handleAssign(a.theme_animation_id, name, tid)}
                        onUnassign={a.assignment_id ? () => handleUnassign(a.assignment_id!) : undefined}
                        onSetPriority={a.animation_type === 'queue' ? ((name, tid) => handleSetPriorityGift(a.theme_animation_id, name, tid)) : undefined}
                      />
                    );
                    if (a.animation_type === 'queue') {
                      return (
                        <div key={a.theme_animation_id} style={{ gridColumn: 'span 2' }}>{card}</div>
                      );
                    }
                    return <React.Fragment key={a.theme_animation_id}>{card}</React.Fragment>;
                  })}
                  {autoAnims.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', color: '#cbd5e1', fontSize: 12, textAlign: 'center', padding: '12px 0', border: '1px dashed #e2e8f0', borderRadius: 8 }}>
                      Aucune animation automatique dans ce thème.
                    </div>
                  )}
                </div>

                {assignments.length === 0 && (
                  <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                    Ce thème n'a pas encore d'animations configurées.
                  </div>
                )}
              </div>
            );
          })()}

          {desktopContent === 'game' && (
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ color: '#0f172a', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
                  Paramétrage du jeu
                </div>
                <div style={{ color: '#64748b', fontSize: 13 }}>
                  {activeGame ? activeGame.name : 'Sélectionne un jeu dans la colonne de gauche.'}
                </div>
              </div>
              {activeGameId && (
                <button onClick={openAddGameAction}
                  style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', borderRadius: 8, padding: '8px 11px', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  + Action
                </button>
              )}
            </div>
          )}

          {/* ── Actions du jeu ── */}
          {desktopContent === 'game' && activeGameId && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
              {gameActions.length === 0 && <p style={{ color: '#94a3b8', fontSize: 13, gridColumn: '1/-1' }}>Aucune action pour ce jeu.</p>}
              {gameActions.flatMap(action => {
                const rules = giftsByAction[action.id] || [];
                const cards = rules.map(r => (
                  <ActionGiftCard
                    key={`rule-${r.id}`}
                    action={action}
                    rule={r}
                    onDelete={() => handleDeleteGameRule(r.id)}
                    onEditAction={() => openEditGameAction(action)}
                    onDeleteAction={action.streamer_id ? () => handleDeleteGameAction(action) : undefined}
                  />
                ));
                cards.push(
                  <ActionAddCard
                    key={`add-${action.id}`}
                    action={action}
                    openPicker={openPicker}
                    onAdd={(name, tid) => handleAddGameRule(action.id, name, tid)}
                    onEditAction={() => openEditGameAction(action)}
                    onDeleteAction={action.streamer_id ? () => handleDeleteGameAction(action) : undefined}
                  />
                );
                return cards;
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Colonne droite : prévisualisation unifiée ── */}
      {(() => {
        const sel = selectedManual; // non-text_dynamic manual
        const hasAuto = !!previewAnim;
        const hasManual = !!sel;
        const canvasAnim = hasAuto ? previewAnim : hasManual ? sel : null;
        const isQueue = canvasAnim?.animation_type === 'queue';
        const isVideo = !isQueue && (canvasAnim?.animation_type === 'video' || (hasAuto && !canvasAnim?.animation_type));
        const isText = canvasAnim?.animation_type === 'text';
        const isAudio = canvasAnim?.animation_type === 'audio';
        const manualVol = sel ? (manualVolumes[sel.theme_animation_id] ?? 0.8) : volume;
        const canPlay = !!(previewAnim && isVideo) || !!(sel && sel.animation_type === 'video');
        const canTrigger = hasAuto || hasManual;

        return (
          <div style={{ width: 260, flexShrink: 0, borderLeft: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

            {/* Header */}
            <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <div style={{ color: hasManual ? '#7c3aed' : '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {hasManual ? `🎛️ ${sel!.name}` : 'Prévisualisation'}
              </div>
            </div>

            {/* Canvas */}
            <div style={{ padding: '14px 16px 10px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div ref={overlayRef} style={{ position: 'relative', width: 200, height: 356, background: '#0a0a0a', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                  <style dangerouslySetInnerHTML={{ __html: `
                    @keyframes prev-fade{from{opacity:0}to{opacity:1}}
                    @keyframes prev-scroll-left{from{transform:translateX(110%)}to{transform:translateX(-110%)}}
                    @keyframes prev-scroll-right{from{transform:translateX(-110%)}to{transform:translateX(110%)}}
                    @keyframes prev-scroll-up{from{transform:translateY(110%)}to{transform:translateY(-110%)}}
                    @keyframes prev-scroll-down{from{transform:translateY(-110%)}to{transform:translateY(110%)}}
                    @keyframes prev-typewriter{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0% 0 0)}}
                    @keyframes prev-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
                    @keyframes prev-zoom{from{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}
                  ` }} />
                  {previewOverlayUrl && (
                    <img src={previewOverlayUrl} alt="overlay"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: 1 }} />
                  )}
                  {previewTextAnim && (
                    <div key={previewTextAnim.key} style={{ position: 'absolute', left: `${previewTextAnim.pos_x}%`, top: `${previewTextAnim.pos_y}%`, width: `${previewTextAnim.pos_w}%`, height: `${previewTextAnim.pos_h}%`, zIndex: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: previewTextAnim.color, fontSize: Math.min(previewTextAnim.size * 0.28, 16) + 'px', fontFamily: previewTextAnim.font, whiteSpace: 'nowrap', animation: `prev-${previewTextAnim.effect} ${previewTextAnim.duration / 1000}s ease ${previewTextAnim.loops} ${previewTextAnim.loops > 1 ? 'none' : 'forwards'}` }}>
                        {previewTextAnim.text}
                      </span>
                    </div>
                  )}
                  {isAudio ? (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <span style={{ fontSize: 44 }}>🔊</span>
                      <span style={{ color: '#16a34a', fontSize: 10, textAlign: 'center', padding: '0 12px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>{sel?.animation_file}</span>
                    </div>
                  ) : canvasAnim && (isVideo || isText) ? (
                    <div style={{ position: 'absolute', left: `${canvasAnim.pos_x}%`, top: `${canvasAnim.pos_y}%`, width: `${canvasAnim.pos_w}%`, height: `${canvasAnim.pos_h}%`, zIndex: 2, border: `1px dashed ${hasManual ? 'rgba(124,58,237,0.6)' : 'rgba(37,99,235,0.6)'}`, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isVideo ? (
                        <video ref={videoRef} key={canvasAnim.animation_file}
                          src={animationFileUrl(canvasAnim.animation_file)}
                          loop playsInline preload="auto" draggable={false}
                          onLoadedData={() => { const v = videoRef.current; if (!v) return; v.volume = hasManual ? manualVol : volume; v.muted = isMuted; if (shouldAutoPlayRef.current) { shouldAutoPlayRef.current = false; v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false)); } else { v.pause(); setIsPlaying(false); } }}
                          style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none' }} />
                      ) : (
                        <div style={{ color: canvasAnim.text_color || '#fff', fontSize: Math.min((canvasAnim.text_size || 48) * 0.35, 13), textAlign: 'center', padding: 4, wordBreak: 'break-word', fontFamily: canvasAnim.text_font || 'sans-serif' }}>
                          {canvasAnim.text_content || '…'}
                        </div>
                      )}
                    </div>
                  ) : isQueue ? (
                    queueCfg
                      ? <QueuePreview bare config={queueCfg} tick={queueTick} />
                      : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#64748b', fontSize: 10, textAlign: 'center', lineHeight: 1.6 }}>File d'attente<br/>(aucune config — créez<br/>l'élément dans le thème)</span>
                        </div>
                      )
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#444', fontSize: 11, textAlign: 'center', lineHeight: 1.7 }}>Clique ▶ sur une<br/>animation automatique<br/>ou un pad manuel</span>
                    </div>
                  )}
                  {/* Images cadeaux permanentes — toutes les animations avec show_gift_image */}
                  {assignments
                    .filter(a => !!a.show_gift_image && !!a.gift_image_url)
                    .map(a => (
                      <img
                        key={`gift-perm-${a.theme_animation_id}`}
                        src={proxyGiftImg(a.gift_image_url!)}
                        alt={a.gift_name ?? 'cadeau'}
                        title={a.gift_name ?? ''}
                        style={{
                          position: 'absolute',
                          left: `${a.gift_img_x ?? 5}%`,
                          top: `${a.gift_img_y ?? 5}%`,
                          width: `${a.gift_img_w ?? 20}%`,
                          height: `${a.gift_img_h ?? 20}%`,
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          zIndex: 6,
                          borderRadius: 8,
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                        }}
                      />
                    ))}
                </div>
              </div>
              {canvasAnim && !isAudio && (
                <div style={{ textAlign: 'center', marginTop: 6, color: '#94a3b8', fontSize: 10, fontFamily: 'monospace' }}>
                  x:{Math.round(canvasAnim.pos_x)}% y:{Math.round(canvasAnim.pos_y)}% — {Math.round(canvasAnim.pos_w)}×{Math.round(canvasAnim.pos_h)}%
                </div>
              )}
            </div>

            {/* Contrôles */}
            <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
              {/* Volume */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <button onClick={() => { const next = !isMuted; setIsMuted(next); if (videoRef.current) videoRef.current.muted = next; }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, color: isMuted ? '#cbd5e1' : '#2563eb', flexShrink: 0 }}>
                  {isMuted ? '🔇' : (hasManual ? manualVol : volume) > 0.5 ? '🔊' : '🔉'}
                </button>
                <input type="range" min={0} max={1} step={0.01} value={isMuted ? 0 : (hasManual ? manualVol : volume)}
                  onChange={e => {
                    const v = parseFloat(e.target.value); setIsMuted(v === 0);
                    if (hasManual && sel) setManualVolumes(prev => ({ ...prev, [sel.theme_animation_id]: v }));
                    else { setVolume(v); }
                    if (videoRef.current) { videoRef.current.volume = v; videoRef.current.muted = v === 0; }
                  }}
                  style={{ flex: 1, accentColor: hasManual ? '#7c3aed' : '#2563eb', cursor: 'pointer' }} />
                <span style={{ color: '#94a3b8', fontSize: 10, fontFamily: 'monospace', width: 28, textAlign: 'right', flexShrink: 0 }}>
                  {isMuted ? '0%' : `${Math.round((hasManual ? manualVol : volume) * 100)}%`}
                </span>
              </div>

              {isQueue ? (
                /* Test File d'attente — anime l'aperçu + démo sur l'overlay */
                <button onClick={async () => {
                  setQueueTick(t => t + 1);
                  setObsStatus('idle');
                  try {
                    await queueTest(streamer.id);
                    setObsStatus('sent'); setObsMsg('Démo lancée !'); setTimeout(() => setObsStatus('idle'), 3000);
                  } catch (err: any) {
                    setObsStatus('error'); setObsMsg(err?.response?.status === 400 ? 'Active le thème' : 'Erreur');
                    setTimeout(() => setObsStatus('idle'), 4000);
                  }
                }}
                  style={{ padding: '10px', borderRadius: 8, border: `1px solid ${obsStatus === 'sent' ? '#bbf7d0' : obsStatus === 'error' ? '#fecaca' : '#c4b5fd'}`, cursor: 'pointer', background: obsStatus === 'sent' ? '#f0fdf4' : obsStatus === 'error' ? '#fef2f2' : '#f5f3ff', color: obsStatus === 'sent' ? '#16a34a' : obsStatus === 'error' ? '#dc2626' : '#7c3aed', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  🧪 {obsStatus === 'sent' ? obsMsg : obsStatus === 'error' ? obsMsg : 'Tester la file d\'attente'}
                </button>
              ) : (
              <>
              {/* Play/Pause */}
              <button onClick={() => { const v = videoRef.current; if (!v) return; if (isPlaying) { v.pause(); setIsPlaying(false); } else { v.play(); setIsPlaying(true); } }}
                disabled={!canPlay}
                style={{ padding: '10px', borderRadius: 8, border: `1px solid ${isPlaying ? '#bbf7d0' : '#e2e8f0'}`, cursor: canPlay ? 'pointer' : 'not-allowed', background: isPlaying ? '#f0fdf4' : '#f8fafc', color: isPlaying ? '#16a34a' : '#64748b', fontSize: 13, fontWeight: 600, opacity: canPlay ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {isPlaying ? '⏸ Pause' : "▶ Jouer l'aperçu"}
              </button>

              {/* Tester sur TikTok PC */}
              <button onClick={async () => {
                setObsStatus('idle');
                try {
                  if (hasAuto && previewAnim) {
                    await previewAnimation(streamer.id, previewAnim.animation_file, previewPos!.x, previewPos!.y, previewPos!.w, previewPos!.h);
                  } else if (hasManual && sel) {
                    await triggerManualAnimation(streamer.id, sel.theme_animation_id, manualVol);
                  }
                  setObsStatus('sent'); setObsMsg('Envoyé !'); setTimeout(() => setObsStatus('idle'), 3000);
                } catch (err: any) {
                  setObsStatus('error'); setObsMsg(err?.response?.status === 503 ? 'Non connecté' : 'Erreur');
                  setTimeout(() => setObsStatus('idle'), 4000);
                }
              }}
                disabled={!canTrigger}
                style={{ padding: '10px', borderRadius: 8, border: `1px solid ${obsStatus === 'sent' ? '#bbf7d0' : obsStatus === 'error' ? '#fecaca' : '#e2e8f0'}`, cursor: canTrigger ? 'pointer' : 'not-allowed', background: obsStatus === 'sent' ? '#f0fdf4' : obsStatus === 'error' ? '#fef2f2' : '#f8fafc', color: obsStatus === 'sent' ? '#16a34a' : obsStatus === 'error' ? '#dc2626' : '#64748b', fontSize: 13, fontWeight: 500, opacity: canTrigger ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                📺 {obsStatus === 'sent' ? obsMsg : obsStatus === 'error' ? obsMsg : 'Tester sur TikTok PC'}
              </button>

              {/* Tester sur TikTok Mobile */}
              <button onClick={async () => {
                setMobileStatus('idle');
                try {
                  if (hasAuto && previewAnim) {
                    await previewAnimationMobile(streamer.id, previewAnim.animation_file, previewPos!.x, previewPos!.y, previewPos!.w, previewPos!.h);
                  } else if (hasManual && sel) {
                    await triggerManualAnimation(streamer.id, sel.theme_animation_id, manualVol);
                  }
                  setMobileStatus('sent'); setMobileMsg('Envoyé !'); setTimeout(() => setMobileStatus('idle'), 3000);
                } catch (err: any) {
                  setMobileStatus('error'); setMobileMsg(err?.response?.status === 503 ? 'Non connecté' : 'Erreur');
                  setTimeout(() => setMobileStatus('idle'), 4000);
                }
              }}
                disabled={!canTrigger}
                style={{ padding: '10px', borderRadius: 8, border: `1px solid ${mobileStatus === 'sent' ? '#bbf7d0' : mobileStatus === 'error' ? '#fecaca' : '#e2e8f0'}`, cursor: canTrigger ? 'pointer' : 'not-allowed', background: mobileStatus === 'sent' ? '#f0fdf4' : mobileStatus === 'error' ? '#fef2f2' : '#f8fafc', color: mobileStatus === 'sent' ? '#16a34a' : mobileStatus === 'error' ? '#dc2626' : '#64748b', fontSize: 13, fontWeight: 500, opacity: canTrigger ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                📱 {mobileStatus === 'sent' ? mobileMsg : mobileStatus === 'error' ? mobileMsg : 'Tester sur TikTok Mobile'}
              </button>
              </>
              )}
            </div>

            {/* Overlay info */}
            <div style={{ borderTop: '1px solid #f1f5f9', padding: '10px 16px', flexShrink: 0 }}>
              <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>Overlay</div>
              <div style={{ color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                {(activeTheme?.overlay_file || streamer.overlay_file) ? (
                  <><span style={{ color: '#16a34a' }}>✓</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTheme?.overlay_file || streamer.overlay_file}</span></>
                ) : (
                  <span style={{ color: '#cbd5e1' }}>Aucun overlay</span>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {pickerOpen && (
        <GiftPicker
          onSelect={(name, id) => { pickerCallbackRef.current?.(name, id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {showGameActionForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 420, maxWidth: '100%', background: '#fff', borderRadius: 12, boxShadow: '0 24px 70px rgba(15,23,42,0.28)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 800 }}>{editingGameAction ? 'Modifier l’action' : 'Nouvelle action'}</div>
                {editingGameAction && !editingGameAction.streamer_id && (
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>Une version personnalisée sera créée pour ce streamer.</div>
                )}
              </div>
              <button onClick={() => setShowGameActionForm(false)} style={{ border: 'none', background: 'transparent', color: '#64748b', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                Nom
                <input value={gameActionForm.name} onChange={e => setGameActionForm(f => ({ ...f, name: e.target.value }))}
                  style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: '#0f172a' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                Description
                <input value={gameActionForm.description} onChange={e => setGameActionForm(f => ({ ...f, description: e.target.value }))}
                  style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: '#0f172a' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                  Type
                  <select value={gameActionForm.action_type} onChange={e => setGameActionForm(f => ({ ...f, action_type: e.target.value }))}
                    style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: '#0f172a', background: '#fff' }}>
                    <option value="key">Clavier</option>
                    <option value="gamepad">Manette</option>
                    <option value="mouse">Souris</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                  Durée ms
                  <input type="number" min={10} value={gameActionForm.duration_ms} onChange={e => setGameActionForm(f => ({ ...f, duration_ms: Number(e.target.value) }))}
                    style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: '#0f172a' }} />
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                Touche / commande (PC clavier)
                <KeyCaptureField
                  value={gameActionForm.action_value}
                  onChange={v => setGameActionForm(f => ({ ...f, action_value: v }))}
                />
              </label>
              {(() => {
                const plats = (activeGame?.platforms || 'pc').split(',');
                const consolePad = plats.includes('ps') || plats.includes('xbox');
                if (!consolePad) return null;
                return (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#475569', fontSize: 11, fontWeight: 700 }}>
                    🎮 Bouton manette (PS / Xbox Remote Play)
                    <GamepadPicker
                      value={gameActionForm.gamepad_value}
                      onChange={v => setGameActionForm(f => ({ ...f, gamepad_value: v }))}
                    />
                  </label>
                );
              })()}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowGameActionForm(false)} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
              <button onClick={saveGameAction} style={{ border: '1px solid #2563eb', background: '#2563eb', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Capture de touche (formulaire d'action de jeu) ────────

function _agentKeyFromEvent(e: KeyboardEvent): string {
  const code = e.code;
  const map: Record<string, string> = {
    Space: 'space', ShiftLeft: 'lshift', ShiftRight: 'rshift',
    ControlLeft: 'lctrl', ControlRight: 'rctrl', AltLeft: 'lalt', AltRight: 'ralt',
    Enter: 'enter', NumpadEnter: 'enter', Tab: 'tab', Backspace: 'backspace',
    Delete: 'delete', ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  };
  if (map[code]) return map[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^Numpad[0-9]$/.test(code)) return code.slice(6);
  if (/^F[0-9]{1,2}$/.test(code)) return code.toLowerCase();
  const k = (e.key || '').toLowerCase();
  return k.length === 1 ? k : '';
}

const KEY_LABELS: Record<string, string> = {
  space: 'Espace', lshift: 'Shift G', rshift: 'Shift D', lctrl: 'Ctrl G', rctrl: 'Ctrl D',
  lalt: 'Alt G', ralt: 'Alt D', enter: 'Entrée', tab: 'Tab', esc: 'Échap',
  backspace: 'Retour', delete: 'Suppr', up: '↑', down: '↓', left: '←', right: '→',
  mouse1: 'Clic gauche', mouse2: 'Clic droit', mouse3: 'Clic milieu',
};
function prettyKey(v: string): string {
  if (!v) return '';
  return KEY_LABELS[v] || (v.length === 1 ? v.toUpperCase() : v);
}

function KeyCaptureField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault(); e.stopPropagation();
      if (e.code === 'Escape') { setOpen(false); return; }
      const v = _agentKeyFromEvent(e);
      if (v) { onChange(v); setOpen(false); }
    };
    const onMouse = (e: MouseEvent) => {
      e.preventDefault();
      const m = e.button === 0 ? 'mouse1' : e.button === 2 ? 'mouse2' : e.button === 1 ? 'mouse3' : '';
      if (m) { onChange(m); setOpen(false); }
    };
    const onCtx = (e: Event) => e.preventDefault();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onMouse, true);
    window.addEventListener('contextmenu', onCtx, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onMouse, true);
      window.removeEventListener('contextmenu', onCtx, true);
    };
  }, [open, onChange]);

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {manual ? (
        <input autoFocus value={value} onChange={e => onChange(e.target.value)}
          placeholder="ex: space, e, lshift, mouse1"
          style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: '#0f172a' }} />
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          style={{ flex: 1, textAlign: 'left', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: 13, fontWeight: 600, background: '#fff', color: value ? '#0f172a' : '#94a3b8', cursor: 'pointer' }}>
          {value ? `${prettyKey(value)}  ·  ${value}` : '⌨️ Cliquer puis appuyer sur la touche'}
        </button>
      )}
      <button type="button" onClick={() => setManual(m => !m)} title={manual ? 'Capture clavier' : 'Saisie manuelle'}
        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, height: 38, padding: '0 10px', fontSize: 12, color: '#64748b', cursor: 'pointer', flexShrink: 0 }}>
        {manual ? '⌨️' : 'abc'}
      </button>

      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 32px', textAlign: 'center', maxWidth: 380, boxShadow: '0 24px 70px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>⌨️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Appuie sur une touche</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              …ou clique (gauche / droit / molette) pour un bouton de souris.<br />
              <span style={{ color: '#94a3b8', fontSize: 12 }}>Échap pour annuler.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sélecteur de bouton manette (PS / Xbox) ───────────────

const GAMEPAD_BUTTONS: { v: string; label: string }[] = [
  { v: 'a', label: '✕ / A' }, { v: 'b', label: '● / B' },
  { v: 'x', label: '■ / X' }, { v: 'y', label: '▲ / Y' },
  { v: 'lb', label: 'L1 / LB' }, { v: 'rb', label: 'R1 / RB' },
  { v: 'lt', label: 'L2 / LT' }, { v: 'rt', label: 'R2 / RT' },
  { v: 'dpad_up', label: 'D-Pad ↑' }, { v: 'dpad_down', label: 'D-Pad ↓' },
  { v: 'dpad_left', label: 'D-Pad ←' }, { v: 'dpad_right', label: 'D-Pad →' },
  { v: 'start', label: 'Options / Menu' }, { v: 'back', label: 'Share / View' },
  { v: 'l3', label: 'L3 (clic G)' }, { v: 'r3', label: 'R3 (clic D)' },
];

function GamepadPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 5 }}>
      {GAMEPAD_BUTTONS.map(b => {
        const sel = value === b.v;
        return (
          <button key={b.v} type="button" onClick={() => onChange(sel ? '' : b.v)}
            style={{ padding: '7px 8px', borderRadius: 7, border: '1px solid', borderColor: sel ? '#7c3aed' : '#e2e8f0', background: sel ? '#f5f3ff' : '#fff', color: sel ? '#7c3aed' : '#475569', fontSize: 12, fontWeight: sel ? 800 : 600, cursor: 'pointer', textAlign: 'center' }}>
            {b.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Mobile accordion view ─────────────────────────────────

interface MobileRuleEditorProps {
  themes: StreamerTheme[];
  streamer: Streamer;
  onToggleTheme: (themeId: number, activate: boolean) => Promise<void>;
  reloadThemes: () => Promise<void>;
  handleAssign: (themeAnimId: number, giftName: string, tiktokGiftId?: number | null) => Promise<void>;
  handleUnassign: (assignmentId: number) => Promise<void>;
  handleSetPriorityGift: (themeAnimId: number, giftName: string | null, tiktokGiftId?: number | null) => Promise<void>;
  openPicker: (cb: (name: string, id?: number | null) => void) => void;
  pickerOpen: boolean;
  pickerCallbackRef: React.MutableRefObject<((name: string, id?: number | null) => void) | null>;
  setPickerOpen: (v: boolean) => void;
}

function MobileRuleEditor({ themes, streamer, onToggleTheme, reloadThemes, handleAssign, handleUnassign, handleSetPriorityGift, openPicker, pickerOpen, pickerCallbackRef, setPickerOpen }: MobileRuleEditorProps) {
  const activeThemeId = themes.find(t => t.is_active === 1)?.id ?? null;
  const [expandedId, setExpandedId] = useState<number | null>(activeThemeId);
  const [assignmentsByTheme, setAssignmentsByTheme] = useState<Record<number, AnimationAssignment[]>>({});
  const [loading, setLoading] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (expandedId) {
      setLoading(l => ({ ...l, [expandedId]: true }));
      getAnimationAssignments(streamer.id, expandedId).then(r => {
        setAssignmentsByTheme(prev => ({ ...prev, [expandedId]: r.data }));
        setLoading(l => ({ ...l, [expandedId]: false }));
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = async (themeId: number) => {
    if (expandedId === themeId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(themeId);
    if (!assignmentsByTheme[themeId]) {
      setLoading(l => ({ ...l, [themeId]: true }));
      const r = await getAnimationAssignments(streamer.id, themeId);
      setAssignmentsByTheme(prev => ({ ...prev, [themeId]: r.data }));
      setLoading(l => ({ ...l, [themeId]: false }));
    }
  };

  const refreshAssignments = async (themeId: number) => {
    const r = await getAnimationAssignments(streamer.id, themeId);
    setAssignmentsByTheme(prev => ({ ...prev, [themeId]: r.data }));
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 80px' }}>
      {themes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>🎨</div>
          <div style={{ fontSize: 13 }}>Aucun thème assigné à ce streamer.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {themes.map(t => {
            const isActive = activeThemeId === t.id;
            const isExpanded = expandedId === t.id;
            const assignments = assignmentsByTheme[t.id] || [];
            const isLoading = loading[t.id];
            const overlayFile = t.overlay_file || streamer.overlay_file;
            const previewOverlayUrl = overlayFile ? overlayUrl(overlayFile) : null;

            return (
              <div key={t.id} style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${isActive ? '#2563eb' : isExpanded ? '#bfdbfe' : '#e2e8f0'}`, overflow: 'hidden', boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.04)' }}>

                {/* Header row */}
                <div onClick={() => toggleExpand(t.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 60, borderRadius: 7, overflow: 'hidden', flexShrink: 0, border: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎨</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={async e => { e.stopPropagation(); await onToggleTheme(t.id, t.is_active !== 1); await reloadThemes(); }}
                      style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${isActive ? '#bbf7d0' : '#e2e8f0'}`, background: isActive ? '#f0fdf4' : '#f8fafc', color: isActive ? '#16a34a' : '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {isActive ? '✓ Sélectionné' : 'Sélectionner'}
                    </button>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2.5"
                      style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 14px' }}>

                    {/* Mini 9:16 preview — centered */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ width: 80, height: 142, background: '#0a0a0a', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', position: 'relative' }}>
                          {previewOverlayUrl ? (
                            <img src={previewOverlayUrl} alt="overlay"
                              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ color: '#444', fontSize: 9, textAlign: 'center' }}>Pas d'overlay</span>
                            </div>
                          )}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 9, textAlign: 'center', marginTop: 4 }}>Aperçu overlay</div>
                      </div>
                    </div>

                    {/* Full-width animation grid — split auto / manual */}
                    {isLoading ? (
                      <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>Chargement…</div>
                    ) : assignments.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>Aucune animation configurée.</div>
                    ) : (() => {
                      const bannerAnims = assignments.filter(a => a.animation_type === 'banner');
                      const autoAnims = assignments.filter(a => (a.trigger_type ?? 'auto') === 'auto' && a.animation_type !== 'mobile_comments' && a.animation_type !== 'banner');
                      const manualAnims = assignments.filter(a => a.trigger_type === 'manual');
                      return (
                        <>
                          {bannerAnims.map(a => (
                            <BannerConfigCard key={a.theme_animation_id} slot={a} streamerId={streamer.id} compact onSaved={() => refreshAssignments(t.id)} />
                          ))}
                          {autoAnims.length > 0 && (
                            <>
                              <div style={{ color: '#94a3b8', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                🎁 Automatique (cadeaux)
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
                                {autoAnims.map(a => {
                                  const card = (
                                    <AnimAssignCard
                                      key={a.theme_animation_id}
                                      slot={a}
                                      isActive={false}
                                      onPreview={() => {}}
                                      openPicker={openPicker}
                                      compact
                                      onAssign={async (name, tid) => {
                                        await handleAssign(a.theme_animation_id, name, tid);
                                        await refreshAssignments(t.id);
                                      }}
                                      onUnassign={a.assignment_id ? async () => {
                                        await handleUnassign(a.assignment_id!);
                                        await refreshAssignments(t.id);
                                      } : undefined}
                                      onSetPriority={a.animation_type === 'queue' ? async (name, tid) => {
                                        await handleSetPriorityGift(a.theme_animation_id, name, tid);
                                        await refreshAssignments(t.id);
                                      } : undefined}
                                    />
                                  );
                                  return a.animation_type === 'queue'
                                    ? <div key={a.theme_animation_id} style={{ gridColumn: 'span 2' }}>{card}</div>
                                    : card;
                                })}
                              </div>
                            </>
                          )}
                          {manualAnims.length > 0 && (
                            <>
                              <div style={{ color: '#94a3b8', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                🎛️ Table de mixage (manuel)
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                                {manualAnims.map(a => (
                                  <ManualInfoCard key={a.theme_animation_id} slot={a} compact />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pickerOpen && (
        <GiftPicker
          onSelect={(name, id) => { pickerCallbackRef.current?.(name, id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ── Section header ────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{label}</div>
      {children}
    </div>
  );
}

// ── Texte dynamique inline (formulaire intégré dans la table de mixage) ──

const SCROLL_EFFECTS = ['scroll-left', 'scroll-right', 'scroll-up', 'scroll-down'];
const FONT_OPTIONS = [
  { v: 'sans-serif', l: 'Sans-serif' }, { v: 'serif', l: 'Serif' },
  { v: 'Impact', l: 'Impact' }, { v: 'Arial Black', l: 'Arial Black' },
  { v: 'Courier New', l: 'Courier New' }, { v: 'Comic Sans MS', l: 'Comic Sans MS' },
  { v: 'Georgia', l: 'Georgia' }, { v: 'Verdana', l: 'Verdana' },
  { v: 'Trebuchet MS', l: 'Trebuchet MS' },
];
const EFFECT_OPTIONS = [
  { v: 'fade', l: 'Fondu' }, { v: 'scroll-left', l: '← Défilement gauche' },
  { v: 'scroll-right', l: '→ Défilement droite' }, { v: 'scroll-up', l: '↑ Défilement haut' },
  { v: 'scroll-down', l: '↓ Défilement bas' }, { v: 'typewriter', l: 'Machine à écrire' },
  { v: 'bounce', l: 'Rebond' }, { v: 'zoom', l: 'Zoom' },
];

function TextDynamicInlineCard({ slot, streamerId, onPreview }: {
  slot: AnimationAssignment;
  streamerId: number;
  onPreview?: (params: { text: string; color: string; size: number; font: string; effect: string; loops: number; duration: number; pos_x: number; pos_y: number; pos_w: number; pos_h: number }) => void;
}) {
  const [text, setText] = useState('');
  const [color, setColor] = useState(slot.text_color || '#ffffff');
  const [font, setFont] = useState(slot.text_font || 'sans-serif');
  const [size, setSize] = useState(slot.text_size || 48);
  const [effect, setEffect] = useState(slot.text_effect || 'fade');
  const [loops, setLoops] = useState(slot.text_loops || 1);
  const [duration, setDuration] = useState(Math.round((slot.text_duration || 5000) / 1000));
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  const handleSend = async () => {
    if (!text.trim()) return;
    setStatus('idle');
    try {
      await sendDynamicText(streamerId, {
        text, text_color: color, text_size: size, text_font: font,
        text_effect: effect, text_loops: loops,
        text_duration: duration * 1000,
        text_bg_color: (slot as any).text_bg_color ?? '#000000',
        text_bg_opacity: (slot as any).text_bg_opacity ?? 0,
        text_bg_anim: (slot as any).text_bg_anim ?? 'none',
        pos_x: slot.pos_x, pos_y: slot.pos_y, pos_w: slot.pos_w, pos_h: slot.pos_h,
      });
      onPreview?.({ text, color, size, font, effect, loops, duration: duration * 1000, pos_x: slot.pos_x, pos_y: slot.pos_y, pos_w: slot.pos_w, pos_h: slot.pos_h });
      setStatus('sent'); setTimeout(() => setStatus('idle'), 3000);
    } catch { setStatus('error'); setTimeout(() => setStatus('idle'), 4000); }
  };

  return (
    <div style={{ gridColumn: '1 / -1', background: '#faf5ff', border: '1.5px solid #c4b5fd', borderRadius: 10, padding: '12px 14px', marginBottom: 6 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ color: '#7c3aed', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⌨️</span> {slot.name}
        </div>
        <button onClick={handleSend} disabled={!text.trim()}
          style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: status === 'sent' ? '#16a34a' : status === 'error' ? '#dc2626' : '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12, cursor: text.trim() ? 'pointer' : 'not-allowed', opacity: text.trim() ? 1 : 0.5 }}>
          {status === 'sent' ? '✓ Envoyé' : status === 'error' ? '✗ Erreur' : '⌨️ Envoyer'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {/* Colonne gauche : textarea + preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Saisir le texte à afficher..."
            rows={3} style={{ width: '100%', borderRadius: 7, border: '1.5px solid #ddd6fe', padding: '7px', fontSize: 13, resize: 'none', fontFamily: font, color: color, boxSizing: 'border-box', background: '#fff', outline: 'none' }} />
          <div style={{ background: '#111', borderRadius: 6, padding: '6px 10px', minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <span style={{ color, fontSize: Math.min(size * 0.22, 16) + 'px', fontFamily: font, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {text || 'Aperçu…'}
            </span>
          </div>
        </div>

        {/* Colonne droite : paramètres */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Couleur + Police */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 30, height: 26, border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
            <select value={font} onChange={e => setFont(e.target.value)}
              style={{ flex: 1, borderRadius: 5, border: '1px solid #ddd6fe', padding: '3px 5px', fontSize: 11 }}>
              {FONT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          {/* Taille */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: '#7c3aed', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>Taille</span>
            <input type="range" min={12} max={120} value={size} onChange={e => setSize(+e.target.value)}
              style={{ flex: 1, accentColor: '#7c3aed' }} />
            <span style={{ color: '#64748b', fontSize: 10, width: 30, textAlign: 'right', flexShrink: 0 }}>{size}px</span>
          </div>
          {/* Effet */}
          <select value={effect} onChange={e => setEffect(e.target.value)}
            style={{ borderRadius: 5, border: '1px solid #ddd6fe', padding: '3px 5px', fontSize: 11 }}>
            {EFFECT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          {/* Boucles + Durée */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: '#7c3aed', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>×</span>
            <input type="number" min={1} max={20} value={loops} onChange={e => setLoops(+e.target.value)}
              style={{ width: 40, borderRadius: 5, border: '1px solid #ddd6fe', padding: '3px 5px', fontSize: 11, textAlign: 'center' }} />
            <span style={{ color: '#94a3b8', fontSize: 10, flexShrink: 0 }}>fois</span>
            <span style={{ color: '#ddd6fe', fontSize: 12 }}>|</span>
            <input type="number" min={1} max={120} value={duration} onChange={e => setDuration(+e.target.value)}
              style={{ width: 40, borderRadius: 5, border: '1px solid #ddd6fe', padding: '3px 5px', fontSize: 11, textAlign: 'center' }} />
            <span style={{ color: '#94a3b8', fontSize: 10, flexShrink: 0 }}>sec/boucle</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pad manuel (vidéo / audio / texte) avec volume ─────────────────────────

function ManualPadCard({ slot, isActive, onTrigger, volume, onVolumeChange }: {
  slot: AnimationAssignment;
  isActive: boolean;
  onTrigger: () => void;
  volume: number | null;
  onVolumeChange: (v: number) => void;
}) {
  const accentColor = slot.animation_type === 'audio' ? '#16a34a'
    : slot.animation_type === 'text' ? '#ea580c' : '#2563eb';
  const bgColor = slot.animation_type === 'audio' ? '#f0fdf4'
    : slot.animation_type === 'text' ? '#fff7ed' : '#eff6ff';
  const icon = typeIcon(slot.animation_type ?? 'video');

  return (
    <div onClick={onTrigger}
      style={{ background: isActive ? accentColor + '22' : bgColor, borderRadius: 10, padding: '8px', display: 'flex', flexDirection: 'column', border: isActive ? `2px solid ${accentColor}` : `1px solid ${accentColor}33`, cursor: 'pointer', transition: 'all 0.15s', boxShadow: isActive ? `0 0 0 3px ${accentColor}18` : 'none' }}>
      <div style={{ fontSize: 24, textAlign: 'center', marginBottom: 4 }}>{icon}</div>
      <div style={{ color: isActive ? accentColor : '#475569', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slot.name}</div>
      {/* Volume slider for video/audio */}
      {volume !== null && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <span style={{ fontSize: 9 }}>{volume === 0 ? '🔇' : '🔊'}</span>
          <input type="range" min={0} max={1} step={0.05} value={volume}
            onChange={e => onVolumeChange(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor, cursor: 'pointer', height: 2 }} />
          <span style={{ color: '#94a3b8', fontSize: 8, width: 20, textAlign: 'right', flexShrink: 0 }}>{Math.round(volume * 100)}%</span>
        </div>
      )}
    </div>
  );
}

// ── Card animation manuelle (info seulement, pas de cadeau) ──────────────

function typeIcon(type: string) {
  if (type === 'audio') return '🔊';
  if (type === 'text') return '📝';
  if (type === 'text_dynamic') return '⌨️';
  if (type === 'banner') return '🪧';
  return '🎬';
}

function typeLabel(type: string) {
  if (type === 'audio') return 'Son';
  if (type === 'text') return 'Texte';
  if (type === 'text_dynamic') return 'Texte dynamique';
  if (type === 'banner') return 'Bannière';
  return 'Vidéo';
}

function ManualInfoCard({ slot, compact, isActive, onSelect }: { slot: AnimationAssignment; compact?: boolean; isActive?: boolean; onSelect?: () => void }) {
  const icon = typeIcon(slot.animation_type ?? 'video');
  const label = typeLabel(slot.animation_type ?? 'video');
  const desc = slot.animation_type === 'text' && slot.text_content
    ? `"${slot.text_content.slice(0, 28)}${slot.text_content.length > 28 ? '…' : ''}"`
    : slot.animation_type === 'text_dynamic'
    ? 'Texte saisi en live'
    : slot.animation_file || '—';

  const accentColor = slot.animation_type === 'audio' ? '#16a34a'
    : slot.animation_type === 'text' ? '#ea580c'
    : slot.animation_type === 'text_dynamic' ? '#7c3aed'
    : '#2563eb';

  const bgColor = slot.animation_type === 'audio' ? '#f0fdf4'
    : slot.animation_type === 'text' ? '#fff7ed'
    : slot.animation_type === 'text_dynamic' ? '#faf5ff'
    : '#eff6ff';

  if (compact) {
    return (
      <div style={{ background: bgColor, borderRadius: 8, padding: 7, display: 'flex', flexDirection: 'column', border: `1px solid ${accentColor}33` }}>
        <div style={{ width: '100%', aspectRatio: '1', borderRadius: 6, background: `${accentColor}18`, marginBottom: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
          {icon}
        </div>
        <div style={{ color: '#475569', fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {slot.name}
        </div>
        <div style={{ color: accentColor, fontSize: 8, textAlign: 'center', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 7, textAlign: 'center', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {desc}
        </div>
      </div>
    );
  }

  return (
    <div onClick={onSelect} style={{ background: isActive ? accentColor + '22' : bgColor, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', border: isActive ? `2px solid ${accentColor}` : `1px solid ${accentColor}44`, cursor: onSelect ? 'pointer' : 'default', boxShadow: isActive ? `0 0 0 3px ${accentColor}22` : 'none', transition: 'all 0.15s' }}>
      <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, background: `${accentColor}18`, marginBottom: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
        {icon}
      </div>
      <div style={{ color: isActive ? accentColor : '#475569', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'center', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {slot.name}
      </div>
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <span style={{ color: accentColor, fontSize: 10, fontWeight: 600, background: `${accentColor}18`, padding: '2px 7px', borderRadius: 5 }}>
          🎛️ {label}
        </span>
      </div>
      <div style={{ color: '#94a3b8', fontSize: 9, textAlign: 'center', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {desc}
      </div>
      {onSelect && (
        <div style={{ marginTop: 6, textAlign: 'center', color: accentColor, fontSize: 9, fontWeight: 600, opacity: 0.7 }}>
          ▶ Cliquer pour contrôler
        </div>
      )}
    </div>
  );
}

// ── Card animation slot ───────────────────────────────────

const bannerEffects = [
  ['scroll-left', 'Gauche'],
  ['scroll-right', 'Droite'],
  ['scroll-up', 'Bas vers haut'],
  ['scroll-down', 'Haut vers bas'],
  ['typewriter', 'Machine a ecrire'],
  ['fade', 'Fondu'],
];

function readBannerConfig(slot: AnimationAssignment) {
  const raw = slot.banner_config || slot.text_content || '';
  try {
    const cfg = JSON.parse(raw);
    if (cfg && typeof cfg === 'object') return cfg;
  } catch {}
  return {
    patterns: [{ text: slot.name || 'Bienvenue sur le live', effect: 'scroll-left', color: '#3b82f6', font: 'sans-serif', size: 42, duration: 9000 }],
    bg_color: '#000000',
    bg_opacity: 0,
    layer: 'below_queue',
  };
}

function BannerConfigCard({ slot, streamerId, onSaved, compact }: {
  slot: AnimationAssignment;
  streamerId: number;
  onSaved: () => Promise<void> | void;
  compact?: boolean;
}) {
  const cfg = readBannerConfig(slot);
  const first = Array.isArray(cfg.patterns) && cfg.patterns.length ? cfg.patterns[0] : {};
  const [enabled, setEnabled] = useState(slot.banner_enabled !== 0 && slot.banner_enabled !== false);
  const [text, setText] = useState(String(first.text || cfg.text || slot.name || ''));
  const [effect, setEffect] = useState(String(first.effect || cfg.effect || 'scroll-left'));
  const [color, setColor] = useState(String(first.color || cfg.color || '#3b82f6'));
  const [font, setFont] = useState(String(first.font || cfg.font || 'sans-serif'));
  const [size, setSize] = useState(Number(first.size || cfg.size || 42));
  const [duration, setDuration] = useState(Math.round(Number(first.duration || cfg.duration || 9000) / 1000));
  const [bgColor, setBgColor] = useState(String(cfg.bg_color || '#000000'));
  const [bgOpacity, setBgOpacity] = useState(Number(cfg.bg_opacity ?? 0));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const next = {
        ...cfg,
        patterns: [{ text, effect, color, font, size, duration: Math.max(1, duration) * 1000 }],
        bg_color: bgColor,
        bg_opacity: bgOpacity,
        layer: 'below_queue',
      };
      await updateBannerConfig(streamerId, slot.theme_animation_id, enabled, next);
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ gridColumn: compact ? 'span 3' : '1 / -1', border: '1px solid #c4b5fd', borderRadius: 12, background: '#faf5ff', padding: compact ? 9 : 12, marginBottom: compact ? 10 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ color: '#6d28d9', fontWeight: 900, fontSize: 13 }}>🪧 {slot.name || 'Bannière'}</div>
          <div style={{ color: '#64748b', fontSize: 11 }}>Texte persistant sur l'overlay, sous la file d'attente.</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0f172a', fontWeight: 800, fontSize: 12 }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Active
        </label>
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Texte de la bannière"
        style={{ width: '100%', minHeight: compact ? 58 : 68, resize: 'vertical', border: '1px solid #ddd6fe', borderRadius: 8, padding: 9, boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 13 }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr 1fr' : '1.2fr 1fr 1fr 0.8fr', gap: 8, marginTop: 8 }}>
        <select value={effect} onChange={e => setEffect(e.target.value)} style={{ border: '1px solid #ddd6fe', borderRadius: 8, padding: 8 }}>
          {bannerEffects.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={font} onChange={e => setFont(e.target.value)} style={{ border: '1px solid #ddd6fe', borderRadius: 8, padding: 8 }}>
          <option value="sans-serif">Sans-serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
          <option value="Impact, sans-serif">Impact</option>
          <option value="Comic Sans MS, cursive">Comic</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11 }}>
          Taille
          <input type="number" min={12} max={180} value={size} onChange={e => setSize(Number(e.target.value))} style={{ width: 64, border: '1px solid #ddd6fe', borderRadius: 8, padding: 8 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11 }}>
          Sec.
          <input type="number" min={1} max={60} value={duration} onChange={e => setDuration(Number(e.target.value))} style={{ width: 58, border: '1px solid #ddd6fe', borderRadius: 8, padding: 8 }} />
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 11 }}>
          Texte <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 42, height: 34 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 11 }}>
          Fond <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} style={{ width: 42, height: 34 }} />
          <input type="number" min={0} max={100} value={bgOpacity} onChange={e => setBgOpacity(Number(e.target.value))} style={{ width: 58, border: '1px solid #ddd6fe', borderRadius: 8, padding: 8 }} />
        </label>
        <button onClick={save} disabled={saving || !text.trim()} style={{ border: 'none', borderRadius: 8, padding: '9px 13px', background: '#7c3aed', color: '#fff', fontWeight: 800, cursor: saving ? 'wait' : 'pointer' }}>
          {saving ? '...' : 'Sauver'}
        </button>
      </div>
    </div>
  );
}

function AnimAssignCard({ slot, streamerId, isActive, onPreview, openPicker, onAssign, onUnassign, onSetPriority, compact }: {
  slot: AnimationAssignment;
  streamerId?: number;
  isActive: boolean;
  onPreview: () => void;
  openPicker: (cb: (name: string, id?: number | null) => void) => void;
  onAssign: (name: string, tid?: number | null) => Promise<void>;
  onUnassign?: () => void;
  onSetPriority?: (name: string | null, tid?: number | null) => Promise<void>;
  compact?: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const hasGift = !!slot.gift_name;
  const isFollow = slot.gift_name === '__follow__';
  const giftLabel = isFollow ? '🔔 Nouvel abonné' : slot.gift_name;
  const giftImg = (!isFollow && slot.gift_image_url) ? proxyGiftImg(slot.gift_image_url) : null;
  const isQueue = slot.animation_type === 'queue';
  const hasPrio = !!slot.priority_gift_name;
  const prioImg = slot.priority_gift_image_url ? proxyGiftImg(slot.priority_gift_image_url) : null;

  const handlePickPriority = () => {
    if (!onSetPriority) return;
    openPicker(async (name, id) => {
      setSavingP(true);
      try { await onSetPriority(name, id); } finally { setSavingP(false); }
    });
  };

  const handlePick = () => {
    openPicker(async (name, id) => {
      setSaving(true);
      try { await onAssign(name, id); } finally { setSaving(false); }
    });
  };

  if (isQueue && onSetPriority) {
    return <QueueAssignCard
      slot={slot}
      streamerId={streamerId}
      isActive={isActive}
      onPreview={onPreview}
      compact={compact}
      hasGift={hasGift}
      giftImg={giftImg}
      hasPrio={hasPrio}
      prioImg={prioImg}
      saving={saving}
      savingP={savingP}
      handlePick={handlePick}
      handlePickPriority={handlePickPriority}
      onUnassign={onUnassign}
      onSetPriority={onSetPriority}
    />;
  }


  if (compact) {
    return (
      <div style={{ background: '#fff', borderRadius: 8, padding: 7, display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        {/* Image */}
        <div style={{ width: '100%', aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: '#f8fafc', marginBottom: 5, flexShrink: 0, border: '1px solid #f1f5f9' }}>
          {giftImg ? (
            <img src={giftImg} alt={slot.gift_name!} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              {isFollow ? '🔔' : hasGift ? '🎁' : '✨'}
            </div>
          )}
        </div>
        {/* Nom animation — 1 ligne max */}
        <div style={{ color: '#94a3b8', fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {slot.name}
        </div>
        {/* Cadeau / déclencheur */}
        <div style={{ marginBottom: 5, textAlign: 'center', minHeight: 16 }}>
          {hasGift ? (
            <span style={{ color: isFollow ? '#16a34a' : '#16a34a', fontSize: 9, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{giftLabel}</span>
          ) : (
            <span style={{ color: '#cbd5e1', fontSize: 9 }}>—</span>
          )}
        </div>
        {/* Boutons */}
        <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>
          <button onClick={handlePick} disabled={saving}
            style={{ flex: 1, padding: '4px 0', borderRadius: 5, border: '1px dashed #cbd5e1', background: '#f8fafc', color: saving ? '#2563eb' : '#64748b', fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            {saving ? '…' : <><span style={{ fontSize: 10 }}>🎁</span><span>{hasGift ? 'Changer' : 'Assigner'}</span></>}
          </button>
          {hasGift && onUnassign && (
            <button onClick={onUnassign}
              style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 10, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>
              ×
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onClick={onPreview}
      style={{ background: isActive ? '#eff6ff' : '#fff', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', cursor: 'pointer', border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`, boxShadow: isActive ? '0 0 0 2px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s' }}>

      {/* Image cadeau ou placeholder */}
      <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: '#f8fafc', marginBottom: 7, flexShrink: 0, border: '1px solid #f1f5f9' }}>
        {giftImg ? (
          <img src={giftImg} alt={slot.gift_name!} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            {isFollow ? '🔔' : hasGift ? '🎁' : '✨'}
          </div>
        )}
      </div>

      {/* Nom de l'animation */}
      <div style={{ color: isActive ? '#2563eb' : '#94a3b8', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'center', marginBottom: 4 }}>
        {slot.name}
      </div>

      {/* Cadeau / déclencheur assigné */}
      {hasGift ? (
        <div style={{ background: isActive ? '#dbeafe' : '#f0fdf4', borderRadius: 5, padding: '3px 6px', marginBottom: 6, textAlign: 'center' }}>
          <span style={{ color: isActive ? '#2563eb' : '#16a34a', fontSize: 10, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {giftLabel}
          </span>
        </div>
      ) : (
        <div style={{ color: '#cbd5e1', fontSize: 10, textAlign: 'center', marginBottom: 6 }}>Non assigné</div>
      )}

      {/* Boutons */}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
        <button onClick={handlePick} disabled={saving}
          style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: '1px dashed #cbd5e1', background: '#f8fafc', color: saving ? '#2563eb' : '#64748b', fontSize: 10, cursor: 'pointer' }}>
          {saving ? '…' : hasGift ? '🎁 Changer' : '🎁 Assigner'}
        </button>
        {hasGift && onUnassign && (
          <button onClick={onUnassign}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// ── Card File d'attente (queue) avec bouton âš™ Paramètres ──

function QueueAssignCard({
  slot, streamerId, isActive, onPreview, compact, hasGift, giftImg, hasPrio, prioImg,
  saving, savingP, handlePick, handlePickPriority, onUnassign, onSetPriority,
}: {
  slot: AnimationAssignment;
  streamerId?: number;
  isActive: boolean;
  onPreview: () => void;
  compact?: boolean;
  hasGift: boolean;
  giftImg: string | null;
  hasPrio: boolean;
  prioImg: string | null;
  saving: boolean;
  savingP: boolean;
  handlePick: () => void;
  handlePickPriority: () => void;
  onUnassign?: () => void;
  onSetPriority?: (name: string | null, tid?: number | null) => Promise<void>;
}) {
  const [showSettings, setShowSettings] = useState(false);
  const isCompactQueue = !!compact;
  const Half = ({ title, img, name, has, onPick, onClear, saving: sv, accent }: {
    title: string; img: string | null; name: string | null | undefined; has: boolean;
    onPick: () => void; onClear?: () => void; saving: boolean; accent: string;
  }) => (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ color: accent, fontSize: isCompactQueue ? 8 : 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', marginBottom: isCompactQueue ? 3 : 4 }}>{title}</div>
      <div style={{ width: '100%', height: isCompactQueue ? 74 : undefined, aspectRatio: isCompactQueue ? undefined : '1', borderRadius: isCompactQueue ? 6 : 8, overflow: 'hidden', background: '#f8fafc', marginBottom: isCompactQueue ? 4 : 6, border: '1px solid #f1f5f9', flexShrink: 0 }}>
        {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isCompactQueue ? 16 : 20 }}>{has ? '🎁' : '✨'}</div>}
      </div>
      <div style={{ background: has ? '#f0fdf4' : 'transparent', borderRadius: 5, padding: isCompactQueue ? '2px 4px' : '3px 4px', marginBottom: isCompactQueue ? 4 : 6, textAlign: 'center', minHeight: isCompactQueue ? 16 : 18 }}>
        <span style={{ color: has ? '#16a34a' : '#cbd5e1', fontSize: isCompactQueue ? 9 : 10, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{has ? name : '—'}</span>
      </div>
      <div style={{ display: 'flex', gap: isCompactQueue ? 3 : 4, justifyContent: 'center', marginTop: 'auto' }} onClick={e => e.stopPropagation()}>
        <button onClick={onPick} disabled={sv}
          style={{ flex: 1, padding: isCompactQueue ? '4px 0' : '5px 0', borderRadius: 6, border: `1px dashed ${accent}66`, background: '#f8fafc', color: accent, fontSize: isCompactQueue ? 9 : 10, cursor: 'pointer' }}>
          {sv ? '…' : has ? 'Changer' : 'Définir'}
        </button>
        {has && onClear && (
          <button onClick={onClear}
            style={{ padding: isCompactQueue ? '4px 6px' : '5px 7px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>×</button>
        )}
      </div>
    </div>
  );
  return (
    <>
      <div onClick={onPreview}
        style={{ background: isActive ? '#eff6ff' : '#fff', borderRadius: isCompactQueue ? 8 : 10, padding: isCompactQueue ? 7 : 10, display: 'flex', flexDirection: 'column', cursor: 'pointer', border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`, boxShadow: isActive ? '0 0 0 2px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s', height: '100%', position: 'relative' }}>
        {/* Bouton âš™ Paramètres en haut à droite */}
        <button
          onClick={e => { e.stopPropagation(); setShowSettings(true); }}
          title="Paramétrage de la file d'attente"
          style={{ position: 'absolute', top: isCompactQueue ? 4 : 6, right: isCompactQueue ? 4 : 6, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: isCompactQueue ? '2px 5px' : '3px 7px', fontSize: isCompactQueue ? 10 : 11, cursor: 'pointer', color: '#475569', lineHeight: 1, zIndex: 1 }}
        >
          âš™
        </button>
        <div style={{ color: isActive ? '#2563eb' : '#94a3b8', fontSize: isCompactQueue ? 8 : 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'center', marginBottom: isCompactQueue ? 5 : 6 }}>
          📋 {slot.name}
        </div>
        <div style={{ display: 'flex', gap: isCompactQueue ? 6 : 10 }}>
          <Half title="🎁 Cadeau" img={giftImg} name={slot.gift_name} has={hasGift}
            onPick={handlePick} onClear={onUnassign} saving={saving} accent="#16a34a" />
          <Half title="⭐ Prioritaire" img={prioImg} name={slot.priority_gift_name} has={hasPrio}
            onPick={handlePickPriority} onClear={() => onSetPriority?.(null, null)} saving={savingP} accent="#a16207" />
        </div>
      </div>

      {showSettings && (
        <QueueSettingsModal slot={slot} streamerId={streamerId} onClose={() => setShowSettings(false)} />
      )}
    </>
  );
}

function QueueSettingsModal({ slot, streamerId, onClose }: { slot: AnimationAssignment; streamerId?: number; onClose: () => void }) {
  const minCoins = (slot as any).queue_min_coins ?? 0;
  const autoHide = (slot as any).queue_auto_hide ?? 30;
  const maxVisible = slot.queue_max_visible ?? 5;

  const [keywords, setKeywords] = useState<string[]>([]);
  const [noiseWords, setNoiseWords] = useState<string[]>([]);
  const [defaults, setDefaults] = useState<{ keywords: string[]; noise_words: string[] } | null>(null);
  const [kwInput, setKwInput] = useState('');
  const [nwInput, setNwInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  // ── TTS par streamer ──
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [ttsVoiceId, setTtsVoiceId] = useState<string>('');
  const [ttsVoices, setTtsVoices] = useState<{ voice_id: string; name: string }[]>([]);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsErr, setTtsErr] = useState<string | null>(null);
  const [ttsSavingFlag, setTtsSavingFlag] = useState(false);

  React.useEffect(() => {
    if (!streamerId) return;
    let cancelled = false;
    (async () => {
      setTtsLoading(true); setTtsErr(null);
      try {
        const [cfg, vs] = await Promise.all([
          getStreamerQueueTTS(streamerId),
          getElevenLabsVoices().catch(() => ({ data: { voices: [] as any[] } })),
        ]);
        if (cancelled) return;
        setTtsEnabled(!!cfg.data.enabled);
        setTtsVoiceId(cfg.data.voice_id || '');
        const list = ((vs as any).data?.voices || []) as { voice_id: string; name: string }[];
        setTtsVoices(list);
      } catch (e: any) {
        if (!cancelled) setTtsErr(e?.response?.data?.detail || 'Erreur de chargement TTS');
      } finally {
        if (!cancelled) setTtsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [streamerId]);

  const saveTTS = async (enabled: boolean, voice: string) => {
    if (!streamerId) return;
    setTtsSavingFlag(true);
    try {
      await setStreamerQueueTTS(streamerId, enabled, voice);
    } catch (e: any) {
      setTtsErr(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setTtsSavingFlag(false);
    }
  };

  const handleToggleTTS = async () => {
    const newVal = !ttsEnabled;
    setTtsEnabled(newVal);
    await saveTTS(newVal, ttsVoiceId);
  };
  const handleVoiceChange = async (v: string) => {
    setTtsVoiceId(v);
    await saveTTS(ttsEnabled, v);
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getQuestionQueueKeywords();
        if (cancelled) return;
        setKeywords(r.data.keywords);
        setNoiseWords(r.data.noise_words);
        setDefaults(r.data.defaults);
      } catch (e: any) {
        if (!cancelled) setMsg({ err: e?.response?.data?.detail || 'Chargement impossible' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const addKeyword = () => {
    const v = kwInput.trim().toLowerCase();
    if (!v || keywords.includes(v)) { setKwInput(''); return; }
    setKeywords(k => [...k, v]); setKwInput(''); setDirty(true);
  };
  const removeKeyword = (k: string) => { setKeywords(prev => prev.filter(x => x !== k)); setDirty(true); };
  const addNoise = () => {
    const v = nwInput.trim().toLowerCase();
    if (!v || noiseWords.includes(v)) { setNwInput(''); return; }
    setNoiseWords(k => [...k, v]); setNwInput(''); setDirty(true);
  };
  const removeNoise = (k: string) => { setNoiseWords(prev => prev.filter(x => x !== k)); setDirty(true); };
  const resetDefaults = () => {
    if (!defaults) return;
    if (!window.confirm('Réinitialiser les listes aux valeurs par défaut ?')) return;
    setKeywords([...defaults.keywords]);
    setNoiseWords([...defaults.noise_words]);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await updateQuestionQueueKeywords(keywords, noiseWords);
      setKeywords(r.data.keywords); setNoiseWords(r.data.noise_words);
      setMsg({ ok: 'Enregistré' }); setDirty(false);
      setTimeout(() => setMsg(null), 3000);
    } catch (e: any) {
      setMsg({ err: e?.response?.data?.detail || 'Erreur — admin requis ?' });
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 9999 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 18 }}>📋</span>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
            File d'attente — paramétrage
          </div>
          <button onClick={onClose} aria-label="Fermer"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', padding: 4, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Réglages actuels (lecture seule, viennent du thème) */}
          <section style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>âš™ Réglages de l'animation (thème)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              <SettingTile label="🎁 Cadeau requis" value={slot.gift_name || '— non défini —'} />
              <SettingTile label="⭐ Cadeau prioritaire" value={slot.priority_gift_name || '— aucun —'} />
              <SettingTile label="💎 Coins min." value={String(minCoins)} hint="seuil pour armer un viewer" />
              <SettingTile label="👥 Max. visibles" value={String(maxVisible)} hint="bulles affichées simultanément" />
              <SettingTile label="⏱ Auto-masquage" value={`${autoHide} s`} hint="durée avant disparition" />
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
              Pour modifier ces valeurs : <b>Mon agence → Thèmes → ouvre le thème actif → animation « {slot.name} »</b>.
            </div>
          </section>

          {/* Édition des mots-clés (globale à l'agence) */}
          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>📚 Mots-clés (détection des questions)</span>
              {defaults && (
                <button onClick={resetDefaults}
                  style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>
                  ↺ Défauts
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              Ajoute un mot-clé : si présent dans le message du viewer armé, il contribue à reconnaître une question. Globaux à l'agence.
            </div>
            {loading ? (
              <div style={{ color: '#94a3b8', fontSize: 12 }}>Chargement…</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                    placeholder="ex : amour, est-ce que, savoir…"
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }} />
                  <button onClick={addKeyword}
                    style={{ background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    + Ajouter
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto', padding: 4, background: '#f8fafc', borderRadius: 6 }}>
                  {keywords.map(k => (
                    <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 8px', fontSize: 11 }}>
                      {k}
                      <button onClick={() => removeKeyword(k)} title="Retirer"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                  {keywords.length === 0 && (
                    <span style={{ color: '#94a3b8', fontSize: 11, padding: 8 }}>Aucun mot-clé — la détection ne se fera que sur la présence de « ? ».</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{keywords.length} mot-clé{keywords.length > 1 ? 's' : ''}</div>
              </>
            )}
          </section>

          {/* Mots considérés comme du bruit */}
          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>🔇 Mots du bruit (ignorés)</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              Un message strictement égal à l'un de ces mots est ignoré.
            </div>
            {loading ? (
              <div style={{ color: '#94a3b8', fontSize: 12 }}>Chargement…</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input value={nwInput} onChange={e => setNwInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNoise(); } }}
                    placeholder="ex : bjr, merci, wow, lol…"
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }} />
                  <button onClick={addNoise}
                    style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    + Ajouter
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 150, overflowY: 'auto', padding: 4, background: '#f8fafc', borderRadius: 6 }}>
                  {noiseWords.map(k => (
                    <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 8px', fontSize: 11, color: '#64748b' }}>
                      {k}
                      <button onClick={() => removeNoise(k)} title="Retirer"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Lecture vocale (TTS) — uniquement si on a un streamerId */}
          {streamerId != null && (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>🔊 Lecture vocale des questions (ElevenLabs)</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                Active la lecture à voix haute des questions ajoutées à la file pour ce streamer.
                {ttsVoices.length === 0 && (
                  <> · <span style={{ color: '#dc2626' }}>Clé ElevenLabs non configurée</span> — va dans <b>Paramètres → Applications → ElevenLabs</b>.</>
                )}
              </div>
              {ttsErr && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{ttsErr}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <div onClick={handleToggleTTS}
                    style={{ width: 40, height: 22, borderRadius: 11, background: ttsEnabled ? '#16a34a' : '#d1d5db', position: 'relative', cursor: ttsSavingFlag ? 'wait' : 'pointer', transition: 'background 0.2s', opacity: ttsLoading ? 0.6 : 1 }}>
                    <div style={{ position: 'absolute', top: 2, left: ttsEnabled ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#0f172a', fontWeight: ttsEnabled ? 700 : 500 }}>
                    {ttsEnabled ? 'Activée' : 'Désactivée'}
                  </span>
                </label>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <select value={ttsVoiceId} onChange={e => handleVoiceChange(e.target.value)}
                    disabled={!ttsEnabled || ttsVoices.length === 0 || ttsSavingFlag}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12, background: '#fff', opacity: (!ttsEnabled || ttsVoices.length === 0) ? 0.5 : 1 }}>
                    <option value="">— choisir une voix —</option>
                    {ttsVoices.map(v => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                {ttsSavingFlag && <span style={{ fontSize: 11, color: '#94a3b8' }}>Enregistrement…</span>}
              </div>
            </section>
          )}

          {/* Documentation détection */}
          <section>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>🔎 Logique de détection</div>
            <ol style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 12.5, lineHeight: 1.65 }}>
              <li>Le viewer offre le cadeau requis (ou prioritaire) → armé pour 5 min si <code style={kbd}>coins ≥ queue_min_coins</code>.</li>
              <li>
                Chaque commentaire du viewer armé est analysé : bruit retiré, score calculé.
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  <li><code style={kbd}>?</code> dans le texte → <b>+2</b></li>
                  <li>Mot-clé de la liste → <b>+2</b> · 2ᵉ mot-clé distinct → +1</li>
                  <li>≥ 5 mots → +1 · ≥ 8 mots → +1</li>
                </ul>
                Seuil : score ≥ 2.
              </li>
              <li><b>Prénom + âge en tête</b> détectés et stockés à part — formats : « Sophie 30 ans, … », « 30 Sophie … », « Sophie, … ». Le texte affiché ne contient plus que la question.</li>
              <li>Reconstruction multi-messages (amorces : « j'aimerais savoir », « j'ai une question ») sur une fenêtre de 90 s.</li>
              <li>Anti-doublon : 1 question en attente par viewer, doublons récents rejetés.</li>
            </ol>
          </section>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, background: '#f8fafc', borderRadius: '0 0 14px 14px' }}>
          {msg?.ok && <span style={{ color: '#16a34a', fontSize: 12 }}>✓ {msg.ok}</span>}
          {msg?.err && <span style={{ color: '#dc2626', fontSize: 12 }}>✗ {msg.err}</span>}
          <span style={{ flex: 1 }} />
          <button onClick={onClose}
            style={{ background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            {dirty ? 'Annuler' : 'Fermer'}
          </button>
          <button onClick={handleSave} disabled={saving || !dirty}
            style={{ background: dirty ? '#0ea5e9' : '#cbd5e1', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, cursor: dirty ? 'pointer' : 'not-allowed', color: '#fff', fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const kbd: React.CSSProperties = { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '0 4px', fontSize: 11, fontFamily: 'ui-monospace,Menlo,Consolas,monospace' };

function SettingTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 700, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      {hint && <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// ── Card cadeau assigné à une action ─────────────────────

function ActionGiftCard({ action, rule, onDelete, onEditAction, onDeleteAction }: {
  action: GameAction;
  rule: GiftGameRule;
  onDelete: () => void;
  onEditAction: () => void;
  onDeleteAction?: () => void;
}) {
  const imgUrl = rule.gift_image_url ? proxyGiftImg(rule.gift_image_url) : null;

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>

      {/* Image cadeau */}
      <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: '#f8fafc', marginBottom: 7, flexShrink: 0, border: '1px solid #f1f5f9' }}>
        {imgUrl ? (
          <img src={imgUrl} alt={rule.gift_name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🎁</div>
        )}
      </div>

      {/* Nom de l'action */}
      <div style={{ color: '#94a3b8', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'center', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {action.name}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 6 }}>
        <span style={{ color: action.streamer_id ? '#16a34a' : '#94a3b8', fontSize: 9, fontWeight: 700 }}>
          {action.streamer_id ? 'Perso' : 'Défaut'}
        </span>
        <button onClick={onEditAction} title="Modifier l'action"
          style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 5, padding: '2px 5px', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>✏️</button>
        {onDeleteAction && (
          <button onClick={onDeleteAction} title="Supprimer l'action personnalisée"
            style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 5, padding: '2px 5px', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>🗑</button>
        )}
      </div>

      {/* Cadeau assigné */}
      <div style={{ background: '#f0fdf4', borderRadius: 5, padding: '3px 6px', marginBottom: 6, textAlign: 'center' }}>
        <span style={{ color: '#16a34a', fontSize: 10, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rule.gift_name}
        </span>
      </div>

      {/* Bouton suppression */}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        <button onClick={onDelete}
          style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 10, cursor: 'pointer' }}>
          × Retirer
        </button>
      </div>
    </div>
  );
}

// ── Card ajout cadeau pour une action ────────────────────

function ActionAddCard({ action, openPicker, onAdd, onEditAction, onDeleteAction }: {
  action: GameAction;
  openPicker: (cb: (name: string, id?: number | null) => void) => void;
  onAdd: (giftName: string, tiktokGiftId?: number | null) => Promise<void>;
  onEditAction: () => void;
  onDeleteAction?: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const handlePick = () => {
    openPicker(async (name, id) => {
      setSaving(true);
      try { await onAdd(name, id); } finally { setSaving(false); }
    });
  };

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', border: '1px dashed #cbd5e1', boxShadow: 'none' }}>

      {/* Placeholder image */}
      <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, background: '#f8fafc', marginBottom: 7, flexShrink: 0, border: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#cbd5e1' }}>
        ✨
      </div>

      {/* Nom de l'action */}
      <div style={{ color: '#94a3b8', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'center', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {action.name}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 6 }}>
        <span style={{ color: action.streamer_id ? '#16a34a' : '#94a3b8', fontSize: 9, fontWeight: 700 }}>
          {action.streamer_id ? 'Perso' : 'Défaut'}
        </span>
        <button onClick={onEditAction} title="Modifier l'action"
          style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 5, padding: '2px 5px', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>✏️</button>
        {onDeleteAction && (
          <button onClick={onDeleteAction} title="Supprimer l'action personnalisée"
            style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 5, padding: '2px 5px', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>🗑</button>
        )}
      </div>

      {/* Key badge */}
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <span style={{ color: '#94a3b8', fontSize: 10, fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
          {action.action_value}
        </span>
      </div>

      {/* Bouton assignation */}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        <button onClick={handlePick} disabled={saving}
          style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: '1px dashed #cbd5e1', background: '#f8fafc', color: saving ? '#2563eb' : '#64748b', fontSize: 10, cursor: 'pointer' }}>
          {saving ? '…' : '🎁 Assigner'}
        </button>
      </div>
    </div>
  );
}

